import React, { FormEvent, useMemo, useState } from 'react';

import { Link as RouterLink } from '~/lib/react-router-dom';

import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useLuxPlusOffersBySavings } from '~/hooks/apis/loyalty/useLuxPlusOffersBySavings';

import {
  LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES,
  LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPE_LABELS,
  LUX_PLUS_REVIEW_SAVING_THRESHOLDS,
  LuxPlusHotelOfferType,
  LuxPlusOfferBySavings,
  LuxPlusOffersBySavingsParams,
} from '~/services/SearchService';

import currencyFormatter from '~/utils/currencyFormatter';

const DEFAULT_FILTERS: Required<Pick<LuxPlusOffersBySavingsParams, 'brand' | 'region' | 'limit'>> = {
  brand: 'luxuryescapes',
  region: 'AU',
  limit: 1000,
};

interface FilterFormState {
  priceMin: string;
  priceMax: string;
  savingMin: string;
  savingMax: string;
  limit: string;
  hotelTypes: Array<LuxPlusHotelOfferType>;
  reviewStatus: NonNullable<LuxPlusOffersBySavingsParams['reviewStatus']>;
}

const DEFAULT_FORM_STATE: FilterFormState = {
  priceMin: '',
  priceMax: '',
  savingMin: '',
  savingMax: '',
  limit: String(DEFAULT_FILTERS.limit),
  hotelTypes: LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES,
  reviewStatus: 'all',
};

const HOTEL_TYPE_SELECT_ALL_VALUE = 'all';

function formatPriceRange(priceMin: number, priceMax?: number) {
  if (priceMax === undefined) {
    return `${currencyFormatter('AUD', priceMin)}+`;
  }

  return `${currencyFormatter('AUD', priceMin)} - ${currencyFormatter('AUD', priceMax)}`;
}

function optionalNumber(value: string) {
  if (value.trim() === '') {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

function toSearchParams(formState: FilterFormState): LuxPlusOffersBySavingsParams {
  return {
    brand: DEFAULT_FILTERS.brand,
    region: DEFAULT_FILTERS.region,
    priceMin: optionalNumber(formState.priceMin),
    priceMax: optionalNumber(formState.priceMax),
    savingMin: optionalNumber(formState.savingMin),
    savingMax: optionalNumber(formState.savingMax),
    limit: optionalNumber(formState.limit),
    hotelTypes: formState.hotelTypes,
    reviewStatus: formState.reviewStatus,
  };
}

function formatRowCurrency(row: LuxPlusOfferBySavings, value: number) {
  return currencyFormatter(row.currencyCode, value);
}

function ReviewStatusGuideStep({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5 }}>
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          mr: 1.5,
          mt: 0.25,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <ListItemText
        primary={
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            {title}
          </Typography>
        }
        secondary={
          <Typography variant="body2" component="div" color="text.secondary">
            {children}
          </Typography>
        }
      />
    </ListItem>
  );
}

function ReviewStatusGuideModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>How flagged review savings work</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Offers are flagged when the displayed non-member lead package price falls into a price bracket and the
          LuxPlus+ saving is equal to or below the suggested minimum saving for that bracket.
        </Typography>

        <List disablePadding>
          <ReviewStatusGuideStep number={1} title="Review status filter">
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
              <li>
                <strong>All</strong> shows every offer matching the other filters.
              </li>
              <li>
                <strong>Flagged for review</strong> shows offers with low savings for their price bracket.
              </li>
              <li>
                <strong>Not flagged</strong> shows offers above the suggested saving threshold.
              </li>
            </Box>
          </ReviewStatusGuideStep>

          <ReviewStatusGuideStep number={2} title="Suggested saving thresholds">
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
              {LUX_PLUS_REVIEW_SAVING_THRESHOLDS.map(({ priceMin, priceMax, savingThreshold }) => (
                <li key={`${priceMin}-${priceMax ?? 'up'}`}>
                  <strong>{formatPriceRange(priceMin, priceMax)}</strong> package price: flagged when saving is{' '}
                  <strong>{currencyFormatter('AUD', savingThreshold)} or less</strong>.
                </li>
              ))}
            </Box>
          </ReviewStatusGuideStep>
        </List>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function OffersBySavingsPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;

  const [formState, setFormState] = useState<FilterFormState>(DEFAULT_FORM_STATE);
  const [isReviewGuideOpen, setIsReviewGuideOpen] = useState(false);
  const [searchParams, setSearchParams] = useState<LuxPlusOffersBySavingsParams>(() =>
    toSearchParams(DEFAULT_FORM_STATE),
  );

  const offersQuery = useLuxPlusOffersBySavings({
    params: searchParams,
  });

  const columns = useMemo<Array<GridColDef<LuxPlusOfferBySavings>>>(
    () => [
      {
        field: 'offerName',
        headerName: 'Offer Name',
        flex: 2,
        minWidth: 280,
      },
      {
        field: 'offerId',
        headerName: 'Offer ID',
        flex: 1.2,
        minWidth: 220,
        renderCell: (params) => (
          <Link component={RouterLink} to={`/offers/${params.row.offerId}`} underline="hover">
            {params.row.offerId}
          </Link>
        ),
      },
      {
        field: 'hotelType',
        headerName: 'Hotel type',
        flex: 0.8,
        minWidth: 140,
      },
      {
        field: 'nonMemberPrice',
        headerName: 'Non-Member Price',
        flex: 0.8,
        minWidth: 160,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => formatRowCurrency(params.row, params.row.nonMemberPrice),
      },
      {
        field: 'luxPlusPrice',
        headerName: 'LuxPlus+ Price',
        flex: 0.8,
        minWidth: 160,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => formatRowCurrency(params.row, params.row.luxPlusPrice),
      },
      {
        field: 'saving',
        headerName: 'Saving',
        flex: 0.7,
        minWidth: 140,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => formatRowCurrency(params.row, params.row.saving),
      },
      {
        field: 'flaggedForReview',
        headerName: 'Review',
        flex: 0.9,
        minWidth: 170,
        renderCell: (params) =>
          params.row.flaggedForReview ? (
            <Chip
              label="Flagged for review"
              color="warning"
              size="small"
              title={`Saving is at or below ${formatRowCurrency(params.row, params.row.reviewSavingThreshold ?? 0)}`}
            />
          ) : (
            <Chip label="OK" color="success" size="small" variant="outlined" />
          ),
      },
    ],
    [],
  );

  const rows = offersQuery.data?.result.offers ?? [];
  const total = offersQuery.data?.result.total ?? 0;

  function handleChange(field: keyof FilterFormState) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((current) => ({
        ...current,
        [field]: event.target.value,
      }));
    };
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSearchParams(toSearchParams(formState));
  }

  function handleHotelTypesChange(event: SelectChangeEvent<Array<LuxPlusHotelOfferType>>) {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;

    if (selectedValues.includes(HOTEL_TYPE_SELECT_ALL_VALUE as LuxPlusHotelOfferType)) {
      setFormState((current) => ({
        ...current,
        hotelTypes:
          current.hotelTypes.length === LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES.length
            ? []
            : LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES,
      }));
      return;
    }

    setFormState((current) => ({
      ...current,
      hotelTypes: selectedValues as Array<LuxPlusHotelOfferType>,
    }));
  }

  function handleReviewStatusChange(event: SelectChangeEvent<FilterFormState['reviewStatus']>) {
    setFormState((current) => ({
      ...current,
      reviewStatus: event.target.value as FilterFormState['reviewStatus'],
    }));
  }

  function handleReset() {
    setFormState(DEFAULT_FORM_STATE);
    setSearchParams(toSearchParams(DEFAULT_FORM_STATE));
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 1.25,
          mb: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: 1.2,
            background: `linear-gradient(135deg, ${themeColor}, ${alpha(themeColor, 0.7)})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Offers by Savings
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Find LuxPlus+ hotel offers where the displayed lead package price is high but the member saving is low.
        </Typography>
      </Paper>

      <Paper component="form" onSubmit={handleSubmit} variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            label="Price min"
            type="number"
            value={formState.priceMin}
            onChange={handleChange('priceMin')}
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ width: { xs: '100%', md: 160 } }}
          />
          <TextField
            label="Price max"
            type="number"
            value={formState.priceMax}
            onChange={handleChange('priceMax')}
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ width: { xs: '100%', md: 160 } }}
          />
          <TextField
            label="Saving min"
            type="number"
            value={formState.savingMin}
            onChange={handleChange('savingMin')}
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ width: { xs: '100%', md: 160 } }}
          />
          <TextField
            label="Saving max"
            type="number"
            value={formState.savingMax}
            onChange={handleChange('savingMax')}
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ width: { xs: '100%', md: 160 } }}
          />
          <FormControl sx={{ width: { xs: '100%', md: 260 } }}>
            <InputLabel id="lux-plus-hotel-type-filter-label">Hotel type</InputLabel>
            <Select<Array<LuxPlusHotelOfferType>>
              labelId="lux-plus-hotel-type-filter-label"
              multiple
              value={formState.hotelTypes}
              onChange={handleHotelTypesChange}
              input={<OutlinedInput label="Hotel type" />}
              renderValue={(selected) =>
                selected.map((hotelType) => LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPE_LABELS[hotelType]).join(', ')
              }
            >
              <MenuItem value={HOTEL_TYPE_SELECT_ALL_VALUE}>
                <Checkbox
                  checked={formState.hotelTypes.length === LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES.length}
                  indeterminate={
                    formState.hotelTypes.length > 0 &&
                    formState.hotelTypes.length < LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES.length
                  }
                />
                <ListItemText primary="Select all" />
              </MenuItem>
              {LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPES.map((hotelType) => (
                <MenuItem key={hotelType} value={hotelType}>
                  <Checkbox checked={formState.hotelTypes.includes(hotelType)} />
                  <ListItemText primary={LUX_PLUS_OFFERS_BY_SAVINGS_HOTEL_TYPE_LABELS[hotelType]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: { xs: '100%', md: 190 } }}>
            <InputLabel id="lux-plus-review-status-filter-label">
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                Review status
                <IconButton
                  aria-label="Review status savings thresholds"
                  size="small"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsReviewGuideOpen(true);
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  sx={(theme) => ({
                    p: 0,
                    width: 18,
                    height: 18,
                    color: 'action.active',
                    bgcolor: theme.palette.background.paper,
                    '&:hover': {
                      bgcolor: theme.palette.background.paper,
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 16,
                    },
                  })}
                >
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Box>
            </InputLabel>
            <Select
              labelId="lux-plus-review-status-filter-label"
              value={formState.reviewStatus}
              label="Review status    "
              onChange={handleReviewStatusChange}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="flagged">Flagged for review</MenuItem>
              <MenuItem value="not_flagged">Not flagged</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Search limit"
            type="number"
            value={formState.limit}
            onChange={handleChange('limit')}
            slotProps={{ htmlInput: { min: 1, max: 2000 } }}
            sx={{ width: { xs: '100%', md: 140 } }}
          />
          <Button variant="contained" type="submit">
            Search
          </Button>
          <Button variant="text" onClick={handleReset}>
            Reset
          </Button>
        </Stack>
      </Paper>

      {offersQuery.error instanceof Error && <Alert severity="error">{offersQuery.error.message}</Alert>}

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1, px: 1 }}>
          {total} offers found
        </Typography>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.offerId}
          loading={offersQuery.isFetching}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
            sorting: {
              sortModel: [{ field: 'saving', sort: 'asc' }],
            },
          }}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>
      <ReviewStatusGuideModal open={isReviewGuideOpen} onClose={() => setIsReviewGuideOpen(false)} />
    </Box>
  );
}
